import React, { useState } from 'react';
import styled from 'styled-components';
import NumberFormat from 'react-number-format';

import { Typography } from 'src/typography';
import { Button, TextInput } from 'components/core';
import useTranslation from 'hooks/use-translation';

const NANP_DIGIT_COUNT = 10;

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Client-side check for a complete 10-digit number -- catches blank/short input, but
 * doesn't enforce NANP structural rules (area code can't start with 0/1, N11 codes,
 * etc.), so a structurally-invalid-but-10-digit number can still reach the backend
 * and come back as `invalid_number` (see `ChangeNumberErrorKind`).
 */
export function isValidNanpNumber(value: string): boolean {
  return digitsOnly(value).length === NANP_DIGIT_COUNT;
}

export type ChangeNumberErrorKind = 'generic' | 'invalid_number' | 'throttled' | 'write_failed' | null;

export type ChangeNumberProps = {
  /** `"Change your phone number"` from leg 1, or leg 2's `"This phone number is connected to another account"`. */
  title: string;
  onBack: () => void;
  onSubmit: (phone: string) => void;
  isSubmitting?: boolean;
  errorKind?: ChangeNumberErrorKind;
  prompt?: string;
  submitLabel?: string;
  headerIcon?: React.ReactNode;
  showBackLink?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
};

/**
 * Shared by leg 1 (this PR) and leg 2 (PR 3, reusing this component with a different
 * `title`). The input always starts empty -- we never have and never render the full
 * on-file number.
 */
export const ChangeNumber = ({
  title,
  onBack,
  onSubmit,
  isSubmitting = false,
  errorKind = null,
  prompt,
  submitLabel,
  headerIcon,
  showBackLink = true,
  onCancel,
  cancelLabel,
}: ChangeNumberProps): JSX.Element => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const isValid = isValidNanpNumber(value);

  const errorMessage = (() => {
    switch (errorKind) {
      case 'throttled':
        return t('phoneVerificationNudge.changeNumberThrottled', 'Please try again later.');
      case 'write_failed':
        return t('phoneVerificationNudge.changeNumberWriteFailed', 'Something went wrong. Please try again.');
      case 'invalid_number':
        return t(
          'phoneVerificationNudge.changeNumberInvalid',
          "That doesn't look like a valid phone number. Please check it and try again."
        );
      case 'generic':
        return t('phoneVerificationNudge.changeNumberGenericError', 'Something went wrong. Please try again.');
      case null:
      default:
        return null;
    }
  })();

  const handleContinue = (): void => {
    if (isValid && !isSubmitting) {
      onSubmit(digitsOnly(value));
    }
  };

  const isIntro = headerIcon !== undefined;

  return (
    <Container $isIntro={isIntro} data-testid='phone-verification-nudge-change-number'>
      <Heading $isIntro={isIntro} size='medium' weight='bold' tag='h2'>
        {title}
      </Heading>
      {headerIcon}
      <Prompt>{prompt ?? t('phoneVerificationNudge.changeNumberPrompt', 'Enter a new phone number:')}</Prompt>
      <InputContainer $isIntro={isIntro} data-testid='phone-verification-nudge-change-number-input-container'>
        <NumberFormat
          displayType='input'
          customInput={PaddedTextInput}
          format='(###) ###-####'
          placeholder='(___) ___-____'
          mask='_'
          type='tel'
          value={value}
          onValueChange={({ value: nextValue }) => setValue(nextValue)}
          // A placeholder never triggers MUI's floating-label shrink heuristics, and this field
          // always starts empty, so the label would otherwise render on top of the placeholder
          // text while unfocused. The placeholder itself (`(___) ___-____`) already makes the
          // field self-describing when empty, so a permanently-floated label is correct here.
          InputLabelProps={{ shrink: true }}
          label={t('phoneVerificationNudge.phoneNumberLabel', 'Phone number')}
          disabled={isSubmitting}
          error={errorMessage !== null}
          id='phone-verification-nudge-change-number-input'
          data-testid='phone-verification-nudge-change-number-input'
        />
      </InputContainer>
      {errorMessage && (
        <ErrorMessage data-testid='phone-verification-nudge-change-number-error'>{errorMessage}</ErrorMessage>
      )}
      {showBackLink ? (
        <ActionsRow>
          <BackButton
            type='button'
            onClick={onBack}
            disabled={isSubmitting}
            data-testid='phone-verification-nudge-change-number-back'
          >
            {t('phoneVerificationNudge.back', '‹ Back')}
          </BackButton>
          <Button
            onClick={handleContinue}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            data-testid='phone-verification-nudge-change-number-continue'
          >
            {submitLabel ?? t('phoneVerificationNudge.continue', 'Continue')}
          </Button>
        </ActionsRow>
      ) : (
        <IntroActions>
          <Button
            onClick={handleContinue}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            width='264px'
            data-testid='phone-verification-nudge-change-number-continue'
          >
            {submitLabel ?? t('phoneVerificationNudge.continue', 'Continue')}
          </Button>
          {onCancel && (
            <CancelButton
              type='button'
              onClick={onCancel}
              disabled={isSubmitting}
              data-testid='phone-verification-nudge-change-number-cancel'
            >
              {cancelLabel ?? t('phoneVerificationNudge.cancel', 'Cancel')}
            </CancelButton>
          )}
        </IntroActions>
      )}
    </Container>
  );
};

const Container = styled.div<{ $isIntro: boolean }>`
  align-items: ${({ $isIntro }) => ($isIntro ? 'center' : 'stretch')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ $isIntro }) => ($isIntro ? 'width: 100%;' : '')}
`;

// Local-only overrides of the shared TextInput -- all scoped to this feature so nothing
// else that consumes TextInput is affected:
//  - background/border/radius: TextInput's un-"variant"-ed fallback (`smallStyles` in
//    text-input.jsx) renders a pale mint box; match the account page's grey field instead.
//  - `& > input` padding-top: gives the always-shrunk label (see `InputLabelProps={{ shrink:
//    true }}` above) room to sit above the value instead of overlapping it.
//  - the sibling `.MuiInputLabel-shrink` override: `text-input.jsx`'s `StyledInputLabel`
//    positions a shrunk label assuming MUI's notched-outline geometry (which floats it partly
//    above the box, into the notch). This field has no notch (`notched={false}` + `& fieldset
//    { border: none }`), so left unpatched the label floats above the box's top edge instead
//    of sitting inside it. Re-anchor it inside the box, left-aligned with the input text.
const InputContainer = styled.div<{ $isIntro: boolean }>`
  max-width: ${({ $isIntro }) => ($isIntro ? '264px' : 'none')};
  width: 100%;
`;

export const PaddedTextInput = styled(TextInput)`
  background: var(--dcm-phone-verification-nudge-input-bg-color, ${({ theme }) => theme.colors.primaryGrey});
  border: 1px solid var(--dcm-phone-verification-nudge-input-border-color, ${({ theme }) => theme.colors.grey[80]}) !important;
  &&& {
    border-radius: var(
      --dcm-phone-verification-nudge-input-border-radius,
      ${({ theme }) => theme.customized.radius.sm}
    );
  }

  & > input {
    ${({ label }) => label && 'padding-top: 30px !important;'}
  }

  & ~ .MuiInputLabel-root.MuiInputLabel-shrink {
    margin-left: 0 !important;
    transform: translate(14px, 8px) scale(0.75);
  }
`;

const Heading = styled(Typography.Heading)<{ $isIntro: boolean }>`
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
  padding-right: ${({ $isIntro }) => ($isIntro ? '0' : '32px')};
  text-align: ${({ $isIntro }) => ($isIntro ? 'center' : 'left')};
`;

const Prompt = styled.p`
  color: var(--dcm-modal-secondary-text-color, ${({ theme }) => theme.colors.grey[30]});
  font-size: 14px;
  margin: 0;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: var(--dcm-phone-verification-nudge-error-color, ${({ theme }) => theme.colors.red?.[55] ?? '#E53935'});
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const ActionsRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const IntroActions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const BackButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dcm-phone-verification-nudge-back-button-color, ${({ theme }) => theme.colors.grey[40]});
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CancelButton = styled(BackButton)``;
